import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play, ShieldCheck, Sparkles } from "lucide-react";
import HeroCanvas from "@/components/three/HeroCanvas";
import CanvasErrorBoundary from "@/components/three/CanvasErrorBoundary";
import { Counter } from "@/components/landing/primitives";
import { HERO_STATS, MARQUEE } from "@/data/content";

const HERO_LINES = ["The Real Token", "Reimagined on the Blockchain"];
const FULL_HEAD = HERO_LINES.join("\n");
const GOLD_FROM = HERO_LINES[1].indexOf("Blockchain"); // gold-highlight last word of line 2

function Caret() {
  return <span className="inline-block w-[4px] h-[0.82em] align-middle ml-1.5 bg-[var(--gold-2)] animate-pulse rounded-sm" />;
}

function HeadlineTyper() {
  const [n, setN] = useState(0);
  const done = n >= FULL_HEAD.length;

  useEffect(() => {
    if (done) return;
    const ch = FULL_HEAD[n];
    const delay = ch === "\n" ? 260 : 70; // small pause on line break
    const t = setTimeout(() => setN((v) => v + 1), delay);
    return () => clearTimeout(t);
  }, [n, done]);

  const typed = FULL_HEAD.slice(0, n);
  const parts = typed.split("\n");
  const l1 = parts[0] || "";
  const l2 = parts[1] ?? null; // null until newline reached
  const onLine2 = l2 !== null;

  const l2white = onLine2 ? l2.slice(0, GOLD_FROM) : "";
  const l2gold = onLine2 ? l2.slice(GOLD_FROM) : "";

  return (
    <span data-testid="hero-typewriter" className="notranslate" translate="no">
      <span className="block">
        {l1}
        {!onLine2 && !done && <Caret />}
      </span>
      <span className="block min-h-[1.05em]">
        {l2white}
        <span className="gold-text text-glow">{l2gold}</span>
        {onLine2 && <Caret />}
      </span>
    </span>
  );
}

export default function Hero() {
  return (
    <section id="top" className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-28 pb-10">
      {/* Backdrop layers */}
      <div className="absolute inset-0 grid-lines opacity-60" />
      <div className="absolute inset-0 radial-gold" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[radial-gradient(circle,rgba(76,125,255,0.10),transparent_60%)]" />

      {/* 3D canvas */}
      <div className="absolute inset-0 z-0 opacity-90">
        <div className="absolute right-0 top-0 w-full lg:w-[62%] h-full">
          <CanvasErrorBoundary
            fallback={
              <div className="w-full h-full flex items-center justify-center">
                <div className="relative animate-floaty">
                  <div className="absolute inset-0 -m-10 rounded-full bg-[radial-gradient(circle,rgba(244,201,93,0.35),transparent_65%)] blur-2xl" />
                  <img
                    src="/trst-logo.png"
                    alt="TRT coin"
                    className="w-64 h-64 object-contain drop-shadow-[0_0_40px_rgba(244,201,93,0.5)]"
                  />
                </div>
              </div>
            }
          >
            <HeroCanvas />
          </CanvasErrorBoundary>
        </div>
      </div>

      <div className="container-trt relative z-10 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full glass-gold px-4 py-1.5 text-xs font-mono text-[var(--gold-2)] mb-6">
              <Sparkles className="w-3.5 h-3.5" /> AI-POWERED · EVM · RWA
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-hero font-extrabold leading-[1.06] text-3xl sm:text-5xl lg:text-[3.6rem] tracking-tight min-h-[2.2em]"
          >
            <HeadlineTyper />
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 text-[15px] sm:text-lg text-[var(--muted)] max-w-xl leading-relaxed"
          >
            TRT is a next-generation, AI-powered blockchain ecosystem for tokenized real-world assets — combining a Layer-1 chain, RWA technology, DeFi, staking and validator nodes into one investor-grade platform.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a href="#token" className="btn-gold" data-testid="hero-cta-primary">
              Explore the Ecosystem <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#chain" className="btn-ghost" data-testid="hero-cta-secondary">
              <Play className="w-4 h-4" /> How it works
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-6 flex items-center gap-2 text-xs text-[var(--muted)]"
          >
            <ShieldCheck className="w-4 h-4 text-[var(--emerald)]" />
            Non-custodial · Audited contracts · Asset-backed 1:1
          </motion.div>
        </div>

        {/* Spacer for canvas on large screens */}
        <div className="hidden lg:block" />
      </div>

      {/* Stats bar */}
      <div className="container-trt relative z-10 mt-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl overflow-hidden glass" data-testid="hero-stats">
          {HERO_STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
              className="p-5 sm:p-6 bg-[rgba(255,255,255,0.01)]"
            >
              <div className="font-display text-2xl sm:text-3xl font-extrabold gold-text">
                <Counter to={s.to} prefix={s.prefix || ""} suffix={s.suffix || ""} />
              </div>
              <div className="mt-1 text-[11px] sm:text-xs text-[var(--muted)] uppercase tracking-wider font-mono">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Marquee */}
      <div className="relative z-10 mt-12 border-y border-white/5 py-3 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} className="mx-6 text-sm font-mono tracking-widest text-[var(--muted)] flex items-center gap-6">
              {m} <span className="text-[var(--gold-3)]">◆</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
